/*
 * The site server Lambda has never logged a request. Between June and August
 * 2026 it failed 62-77% of invocations on dev, 76% on staging and 10-15% on
 * prod, and every one of those failures reached CloudWatch as nothing but
 * `INIT_START / START / END / REPORT ... Status: timeout`. There was no stack,
 * no route, no clue which code path had stopped - three months of a broken
 * service that could not be read from its own logs.
 *
 * The reason the silence is total is worth stating, because it drives the whole
 * design below: when a Lambda hits its timeout the runtime kills the process. A
 * `finally` block does not run, a rejected promise is never reported, and
 * anything you meant to write on the way out is lost. Instrumentation that only
 * reports on completion therefore reports on exactly the requests that were
 * already fine, and stays quiet for the ones anyone actually wants to see.
 *
 * So the important half of this module is the stall watchdog. Timers fire while
 * the request is still hanging - comfortably before the 45s ceiling - and print
 * what is in flight. A request that dies at 45s still leaves a trail saying
 * which method and path were stuck and how long they had been stuck for.
 *
 * Only the path is logged, never the query string, headers or cookies: this
 * runs on dashboard routes too, where those carry session tokens.
 */

/**
 * Elapsed marks at which an in-flight request is reported as stalled.
 *
 * The last mark has to leave enough room to actually reach CloudWatch before
 * the runtime is killed at 45s, hence 40s rather than something closer. The
 * earlier marks are what distinguish "slow" from "wedged": a request that logs
 * 15s and then completes is a performance problem, one that logs 15/30/40 and
 * then vanishes was killed mid-flight.
 */
const DEFAULT_STALL_MARKS_MS = [15_000, 30_000, 40_000]

/** Completed requests slower than this are logged; below it they are not. */
const DEFAULT_SLOW_REQUEST_MS = 1_000

export type TelemetryLogger = (line: string) => void

export type RequestTelemetryOptions = {
  now?: () => number
  log?: TelemetryLogger
  setTimer?: (fn: () => void, ms: number) => unknown
  clearTimer?: (handle: unknown) => void
  stallMarksMs?: number[]
  slowRequestMs?: number
  stage?: string
  /** Log every completed request, not only slow ones. Off by default. */
  logAll?: boolean
}

export type NextResult = { response?: Response } | undefined

/**
 * Pull the request line out of a Request without touching anything sensitive.
 *
 * `new URL` throws on a malformed URL, and a request malformed enough to do
 * that is precisely the kind worth logging, so the failure degrades to a
 * placeholder rather than taking out the middleware.
 */
function describeRequest(request: Request) {
  const method = request.method || "GET"
  try {
    return { method, path: new URL(request.url).pathname }
  } catch {
    return { method, path: "<unparseable>" }
  }
}

function resolveStage(explicit?: string) {
  return explicit || process.env.PUBLIC_STAGE || process.env.VITE_PUBLIC_STAGE || "unknown"
}

function envInt(name: string) {
  const raw = process.env[name]
  if (!raw) return undefined
  const parsed = Number(raw)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
}

/**
 * One JSON object per line. CloudWatch Logs Insights parses those natively, so
 * a stall can be grouped by path with `stats count() by path` and no regex.
 */
function emit(log: TelemetryLogger, fields: Record<string, unknown>) {
  try {
    log(JSON.stringify(fields))
  } catch {
    // Telemetry must never be the reason a page fails to render.
  }
}

/*
 * Why a heartbeat exists on top of the per-request watchdog.
 *
 * The first cut of this module armed its stall timers inside the request
 * middleware and assumed that was enough. It was not: dev timed out five times
 * at exactly 45,000ms with the watchdog deployed and printed no stall line at
 * all. That leaves exactly three possibilities, and they are not
 * distinguishable from the absence of a log:
 *
 *   1. the hang happens before the request middleware is reached, so the timers
 *      were never armed;
 *   2. the event loop is blocked outright, so no timer of any kind can fire;
 *   3. the request middleware ran and finished, and the time is being spent
 *      somewhere after it.
 *
 * The heartbeat separates them. It is installed at module load, independent of
 * any request, and prints what it sees:
 *
 *   heartbeats stop during the hang        -> the event loop is blocked (2)
 *   heartbeats continue with inflight 0    -> the hang is outside the
 *                                             middleware (1 or 3)
 *   heartbeats continue naming a request   -> the middleware is live and that
 *                                             request is the one stuck
 *
 * It worked - and then it became the bug. The first version of this interval
 * was deliberately left referenced, on the reasoning that "an async handler's
 * response is sent when its promise settles, not when the loop drains, so
 * keeping it referenced costs nothing". That is true of a buffered handler and
 * false of this one. The site runs behind a Lambda Function URL with
 * `InvokeMode: RESPONSE_STREAM`, where the invocation does not complete until
 * the event loop drains. A single referenced timer therefore pinned every
 * invocation to the 45s ceiling: the client got its response in about a second
 * and the sandbox then sat idle until the runtime killed it. Prod went from
 * 5-17% of invocations failing to 97.2% on the day the probe shipped, stayed at
 * 100% for a week, and billed roughly $225 a month to do it. The heartbeat's
 * own output is what proved it, ticking `inflight: 0` for the full 45 seconds.
 *
 * So the interval is now unref'd, and the probe is opt-in rather than on by
 * default. The cost of unref'ing is the one case the original reasoning wanted
 * to catch - a hang with nothing else pending, which stops an unref'd timer and
 * reads as a blocked loop. That case is worth losing: our own timer can no
 * longer be it, and any real stall is something pending, which keeps the loop
 * alive and the heartbeat ticking.
 *
 * Nothing in this Lambda may hold a referenced handle at module scope. If you
 * add anything periodic here, unref it, and assert that in a test.
 */
const HEARTBEAT_INTERVAL_MS = 5_000

type InFlight = { method: string; path: string; started: number }

const inFlight = new Set<InFlight>()
let heartbeat: ReturnType<typeof setInterval> | undefined

export function heartbeatTick(now: () => number, log: TelemetryLogger, stage: string) {
  let oldest: InFlight | undefined
  for (const entry of inFlight) {
    if (!oldest || entry.started < oldest.started) oldest = entry
  }
  emit(log, {
    evt: "site.heartbeat",
    stage,
    inflight: inFlight.size,
    oldestMs: oldest ? now() - oldest.started : 0,
    oldestPath: oldest?.path,
  })
}

/**
 * Drop a timer's hold on the event loop.
 *
 * Typed through `unknown` on purpose: this app's tsconfig lists an explicit
 * `types` array, so `@types/node` is not in scope and `setInterval` resolves to
 * the DOM's `number`. At runtime on Lambda it is a Node `Timeout`, which has
 * `unref()`.
 */
function unrefTimer(timer: unknown) {
  ;(timer as { unref?: () => void })?.unref?.()
}

/**
 * Arm the heartbeat. Off unless `SITE_HEARTBEAT=true`.
 *
 * Opt-in because this is a debugging probe, not telemetry anyone reads in
 * steady state: at a 5s interval it writes about nine lines for every
 * invocation that outlives a tick, on every stage, forever.
 */
export function startHeartbeat(options: RequestTelemetryOptions = {}) {
  if (heartbeat) return heartbeat
  if (process.env.SITE_HEARTBEAT !== "true") return undefined
  const now = options.now ?? Date.now
  const log = options.log ?? ((line: string) => console.log(line))
  const stage = resolveStage(options.stage)
  const every = envInt("SITE_HEARTBEAT_MS") ?? HEARTBEAT_INTERVAL_MS
  heartbeat = setInterval(() => heartbeatTick(now, log, stage), every)
  unrefTimer(heartbeat)
  return heartbeat
}

/** Test seam: forget the module-level interval between cases. */
export function resetTelemetryState() {
  if (heartbeat) clearInterval(heartbeat)
  heartbeat = undefined
  inFlight.clear()
}

export function inFlightCount() {
  return inFlight.size
}

/**
 * Wrap a request, reporting it if it stalls, errors, or runs slowly.
 *
 * Exported separately from the middleware so it can be tested without standing
 * up the TanStack request pipeline.
 */
export async function instrumentRequest(
  request: Request,
  next: () => Promise<NextResult>,
  options: RequestTelemetryOptions = {}
): Promise<NextResult> {
  const now = options.now ?? Date.now
  const log = options.log ?? ((line: string) => console.log(line))
  const setTimer = options.setTimer ?? ((fn: () => void, ms: number) => setTimeout(fn, ms))
  const clearTimer = options.clearTimer ?? ((handle: unknown) => clearTimeout(handle as never))
  const slowRequestMs = options.slowRequestMs ?? envInt("SITE_SLOW_REQUEST_MS") ?? DEFAULT_SLOW_REQUEST_MS
  const stallMarks = options.stallMarksMs ?? DEFAULT_STALL_MARKS_MS
  const logAll = options.logAll ?? process.env.SITE_REQUEST_LOG_ALL === "true"
  const stage = resolveStage(options.stage)

  const { method, path } = describeRequest(request)
  const started = now()
  const entry: InFlight = { method, path, started }
  inFlight.add(entry)

  // Armed before `next()` so a handler that hangs on its very first await is
  // still covered.
  const timers = stallMarks.map((mark) =>
    setTimer(() => {
      emit(log, { evt: "site.request.stalled", stage, method, path, ms: mark })
    }, mark)
  )
  const clearAll = () => {
    for (const timer of timers) clearTimer(timer)
  }

  try {
    const result = await next()
    const ms = now() - started
    const status = result?.response?.status
    if (logAll || ms >= slowRequestMs || (typeof status === "number" && status >= 500)) {
      emit(log, { evt: "site.request", stage, method, path, status, ms })
    }
    return result
  } catch (error) {
    emit(log, {
      evt: "site.request.failed",
      stage,
      method,
      path,
      ms: now() - started,
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
    })
    throw error
  } finally {
    inFlight.delete(entry)
    clearAll()
  }
}
